/**
 * Centralized configuration service that loads config.json once at application startup
 * and provides it throughout the application lifecycle
 */

export interface AppConfig {
  REACT_APP_API_BASE_URL: string;
  MSAL_CONFIG: {
    clientId: string;
    authority: string;
    redirectUri: string;
    scopes: string[];
  };
}

class ConfigService {
  private config: AppConfig | null = null;
  private loading: Promise<AppConfig> | null = null;

  /**
   * Initialize the config service by loading configuration from config.json
   * This should be called once at application startup
   */
  async initialize(): Promise<AppConfig> {
    // If already loading, return the existing promise
    if (this.loading) {
      return this.loading;
    }

    // If already loaded, return the cached config
    if (this.config) {
      return this.config;
    }

    // Start loading configuration
    this.loading = this.loadConfig();
    
    try {
      this.config = await this.loading;
      return this.config;
    } finally {
      this.loading = null;
    }
  }

  /**
   * Load configuration from config.json
   */
  private async loadConfig(): Promise<AppConfig> {
    try {
      const response = await fetch('/config.json');
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }
      
      const config = await response.json();
      
      // Validate required configuration
      if (!config.REACT_APP_API_BASE_URL) {
        throw new Error('REACT_APP_API_BASE_URL is required in config.json');
      }

      return {
        REACT_APP_API_BASE_URL: config.REACT_APP_API_BASE_URL,
        MSAL_CONFIG: {
          clientId: config.MSAL_CONFIG?.clientId || '',
          authority: config.MSAL_CONFIG?.authority || '',
          redirectUri: config.MSAL_CONFIG?.redirectUri || '',
          scopes: config.MSAL_CONFIG?.scopes || ['User.Read', 'openid', 'profile']
        }
      };
    } catch (error) {
      console.error('Failed to load configuration:', error);
      
      // Return default configuration as fallback
      return {
        REACT_APP_API_BASE_URL: 'https://localhost:3001',
        MSAL_CONFIG: {
          clientId: '',
          authority: '',
          redirectUri: '',
          scopes: ['User.Read', 'openid', 'profile']
        }
      };
    }
  }

  /**
   * Get the current configuration
   * Throws an error if config hasn't been initialized
   */
  getConfig(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not initialized. Call configService.initialize() first.');
    }
    return this.config;
  }

  /**
   * Get API base URL
   */
  getApiBaseUrl(): string {
    return this.getConfig().REACT_APP_API_BASE_URL;
  }

  /**
   * Get MSAL configuration
   */
  getMSALConfig() {
    return this.getConfig().MSAL_CONFIG;
  }

  /**
   * Check if configuration is loaded
   */
  isInitialized(): boolean {
    return this.config !== null;
  }

  /**
   * Force reload configuration (useful for development)
   */
  async reload(): Promise<AppConfig> {
    this.config = null;
    this.loading = null;
    return this.initialize();
  }
}

// Export singleton instance
export const configService = new ConfigService();

// Initialize config when this module is imported
// This ensures config is available early in the application lifecycle
export const initializeConfig = () => configService.initialize();